import { useRef, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ReceiptUpload } from "../../hooks/useReceiptUpload";
import { ReceiptThumbnail } from "../ReceiptThumbnail";
import { Tooltip } from "../Tooltip";

interface ReceiptStatusProps {
  receipt: ReceiptUpload;
  clientId: number;
}

/** A review card's receipt: the upload's stage, its error, and the image preview. */
export function ReceiptStatus({ receipt, clientId }: ReceiptStatusProps): ReactNode {
  return (
    <>
      {/* Receipt upload status and preview */}
      <AnimatePresence mode="wait">
        {receipt.stage === "uploading" && (
          <motion.div
            key="uploading"
            style={{
              padding: "8px 12px",
              background: "#f0f9ff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "8px",
            }}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            >
              ⏳
            </motion.span>
            <span>Uploading receipt...</span>
          </motion.div>
        )}

        {receipt.stage === "error" && receipt.error && (
          <motion.div
            key="error"
            style={{
              padding: "8px 12px",
              background: "#fef2f2",
              color: "#dc2626",
              borderRadius: "8px",
              marginTop: "8px",
            }}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <span>{receipt.error}</span>
          </motion.div>
        )}

        {receipt.stage === "success" && receipt.result && (
          <motion.div
            key="success"
            style={{
              padding: "8px 12px",
              background: "#f0fdf4",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "8px",
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <motion.span
              style={{
                fontSize: "1.2rem",
                color: "#16a34a",
                fontWeight: "bold",
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
                delay: 0.1,
              }}
              aria-label="Upload complete"
            >
              ✓
            </motion.span>
            <ReceiptThumbnail receiptId={receipt.result.id} clientId={clientId} />
          </motion.div>
        )}
      </AnimatePresence>

      {receipt.preview && receipt.stage !== "success" && (
        <div
          style={{
            marginTop: "8px",
            borderRadius: "8px",
            overflow: "hidden",
            maxHeight: "120px",
          }}
        >
          <img
            src={receipt.preview}
            alt="Receipt preview"
            style={{ width: "100%", objectFit: "cover" }}
          />
        </div>
      )}
    </>
  );
}

interface ReceiptButtonProps {
  receipt: ReceiptUpload;
}

/** The card's receipt button and its hidden file input. */
export function ReceiptButton({ receipt }: ReceiptButtonProps): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <Tooltip text="Attach a receipt photo to this transaction">
        <motion.button
          type="button"
          className="card-action-btn card-action-receipt"
          onClick={() => inputRef.current?.click()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Attach receipt"
        >
          <span className="card-action-icon">&#128247;</span>
        </motion.button>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        capture="environment"
        onChange={(e) => void receipt.handleChange(e)}
        style={{ display: "none" }}
      />
    </>
  );
}
